/**
 * Lenders Mortgage Insurance (LMI) Premium Rates
 *
 * LMI is required when borrowing more than 80% of the property value.
 * Rates vary by LVR band and loan amount.
 *
 * These are indicative rates based on industry averages.
 * Actual rates vary by insurer (Genworth, QBE, Arch) and lender.
 */

export interface LMIRate {
  lvrMin: number;
  lvrMax: number;
  rates: {
    loanAmountMax: number;
    rate: number; // Percentage of loan amount
  }[];
}

/**
 * LMI premium rates by LVR band
 * Rates are % of loan amount
 */
export const lmiRates: LMIRate[] = [
  {
    lvrMin: 80.01,
    lvrMax: 85,
    rates: [
      { loanAmountMax: 300000, rate: 0.58 },
      { loanAmountMax: 500000, rate: 0.70 },
      { loanAmountMax: 750000, rate: 0.92 },
      { loanAmountMax: 1000000, rate: 1.10 },
      { loanAmountMax: Infinity, rate: 1.25 },
    ],
  },
  {
    lvrMin: 85.01,
    lvrMax: 90,
    rates: [
      { loanAmountMax: 300000, rate: 1.25 },
      { loanAmountMax: 500000, rate: 1.65 },
      { loanAmountMax: 750000, rate: 2.10 },
      { loanAmountMax: 1000000, rate: 2.50 },
      { loanAmountMax: Infinity, rate: 2.85 },
    ],
  },
  {
    lvrMin: 90.01,
    lvrMax: 95,
    rates: [
      { loanAmountMax: 300000, rate: 2.25 },
      { loanAmountMax: 500000, rate: 2.90 },
      { loanAmountMax: 750000, rate: 3.40 },
      { loanAmountMax: 1000000, rate: 3.85 },
      { loanAmountMax: Infinity, rate: 4.20 },
    ],
  },
  {
    lvrMin: 95.01,
    lvrMax: 97,
    rates: [
      { loanAmountMax: 300000, rate: 3.10 },
      { loanAmountMax: 500000, rate: 3.60 },
      { loanAmountMax: 750000, rate: 4.10 },
      { loanAmountMax: Infinity, rate: 4.50 },
    ],
  },
];

/**
 * Calculate LMI premium
 */
export function calculateLMI(loanAmount: number, propertyValue: number): number {
  const lvr = (loanAmount / propertyValue) * 100;

  // No LMI required for LVR <= 80%
  if (lvr <= 80) return 0;

  // Find the applicable rate band
  const rateBand = lmiRates.find((band) => lvr > band.lvrMin && lvr <= band.lvrMax);

  if (!rateBand) return 0;

  // Find the applicable rate within the band
  const rateEntry = rateBand.rates.find((r) => loanAmount <= r.loanAmountMax);

  if (!rateEntry) return 0;

  // Calculate premium
  const premium = loanAmount * (rateEntry.rate / 100);

  // Add stamp duty on LMI (approximately 10% varies by state)
  const stampDutyOnLMI = premium * 0.10;

  return premium + stampDutyOnLMI;
}

/**
 * Get LMI rate percentage for display
 */
export function getLMIRate(loanAmount: number, propertyValue: number): number {
  const lvr = (loanAmount / propertyValue) * 100;

  if (lvr <= 80) return 0;

  const rateBand = lmiRates.find((band) => lvr > band.lvrMin && lvr <= band.lvrMax);
  if (!rateBand) return 0;

  const rateEntry = rateBand.rates.find((r) => loanAmount <= r.loanAmountMax);
  return rateEntry?.rate || 0;
}

/**
 * Ways to avoid LMI
 */
export const lmiAvoidanceStrategies = [
  {
    title: "Save a 20% deposit",
    description: "The most straightforward way - LMI only applies when borrowing more than 80%",
  },
  {
    title: "Family guarantee",
    description: "A parent can use their property as additional security to avoid LMI",
  },
  {
    title: "First Home Guarantee",
    description: "Government scheme allowing 5% deposit without LMI for eligible first home buyers",
  },
  {
    title: "Professional packages",
    description: "Some lenders waive LMI for doctors, lawyers, accountants with 90% LVR",
  },
  {
    title: "Lender promotions",
    description: "Some lenders occasionally offer LMI waivers or discounts",
  },
];
